import Vue from "vue";
import gluedin from "gluedin";
import eliteService from "@/module_user/api/elite-service";
import redirect from "@/module_user/uitls/redirect";
import {notifyError} from "@/module_user/uitls/notification-helper";
import MESSAGES from "@/module_social/models/messages";

export class OrderedMap {

    set(key, value, entries) {
        const index = this._findEntryIndex(key, entries);
        console.log("index: ", index, entries);
        if (index !== -1) {
            // Key exists, update value
            return entries.splice(index, 1, [key, value]);
        } else {
            // Key does not exist, add entry
            const index = Math.max(entries.length - 1, 0);
            return entries.splice(index, 0, [key, value]);
        }
    }

    get(key, entries) {
        const index = this._findEntryIndex(key, entries);
        if (index !== -1) {
            return entries[index][1];
        }
        return undefined;
    }

    delete(key, entries) {
        const index = this._findEntryIndex(key, entries);
        if (index === -1) {
            // Key not found, return original map
            return entries;
        } else {
            // Key found, remove entry
            return entries.splice(index, 1);
        }
    }

    entries(entries) {
        return entries[Symbol.iterator]();
    }

    // Find the index of the entity with the given key
    _findEntryIndex(key, entries) {
        return entries.findIndex(([entryKey]) => entryKey === key);
    }
}

export class OrderedList {

    add(key, list, map) {
        if (!(key in map)) {
            Vue.set(map, key, list.length);
            list.push(key);
        }
    }

    delete(key, list, map) {
        if (key in map) {
            const index = map[key];
            Vue.delete(map, key);
            list.splice(index, 1);
            // Update the position of each item after the deleted item
            for (let i = index; i < list.length; i++) {
                Vue.set(map, list[i], i);
            }
        }
    }

    addAt(key, index, list, map) {
        if (!(key in map) && index >= 0 && index <= list.length) {
            Vue.set(map, key, index);
            list.splice(index, 0, key);
            // Update the position of each item after the inserted item
            for (let i = index + 1; i < list.length; i++) {
                Vue.set(map, list[i], i);
            }
        }
    }

    deleteAt(index, list, map) {
        if (index >= 0 && index < list.length) {
            const key = list[index];
            this.delete(key, list, map);
        }
    }

    deleteBy(key, list, map) {
        if (key in map) {
            const index = map[key];
            this.deleteAt(index, list, map);
        }
    }

    reposition(key, index, list, map) {
        if (key in map && index < list.length && index > -1) {
            this.delete(key, list, map);
            map[key] = index;
            list.splice(index, 0, key);
        }
    }
}

export class Comment {
    constructor({id, description, likeCount, replyCount, status, creatorId, lastUpdated}) {
        Object.defineProperty(this, '_data', {
            value: {
                id,
                description,
                likeCount,
                replyCount,
                status,
                creatorId,
                lastUpdated,
            },
            writable: false,
            enumerable: false,
            configurable: false,
        });

        Object.defineProperties(this, {
            id: {
                enumerable: true,
                get() {
                    return this._data.id;
                },
                set(value) {
                    if (typeof value === "string") {
                        this._data.id = value;
                    } else {
                        throw new TypeError("id must be a string");
                    }
                },
            },
            description: {
                enumerable: true,
                get() {
                    return this._data.description;
                },
                set(value) {
                    if (typeof value === "string") {
                        this._data.description = value;
                    } else {
                        throw new TypeError("description must be a string");
                    }
                },
            },
            likeCount: {
                enumerable: true,
                get() {
                    return this._data.likeCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.likeCount = value;
                    } else {
                        throw new TypeError('likeCount must be a number');
                    }
                }
            },
            replyCount: {
                enumerable: true,
                get() {
                    return this._data.replyCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.replyCount = value;
                    } else {
                        throw new TypeError('replyCount must be a number');
                    }
                }
            },
            status: {
                enumerable: true,
                get() {
                    return this._data.status;
                },
                set(value) {
                    if (typeof value === "string") {
                        this._data.status = value;
                    } else {
                        throw new TypeError("status must be a string");
                    }
                },
            },
            creatorId: {
                enumerable: true,
                get() {
                    return this._data.creatorId;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.creatorId = value;
                    } else {
                        throw new TypeError('creatorId must be a string');
                    }
                }
            },
            lastUpdated: {
                get() {
                    return this._data.lastUpdated;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.lastUpdated = value;
                    } else {
                        throw new TypeError('lastUpdated must be a string');
                    }
                },
                enumerable: true,
            },
        });
    }
}

export class UserProfile {
    constructor({
                    userId,
                    userName,
                    fullName,
                    profileImageUrl,
                    followersCount,
                    followingCount,
                    postCount,
                    description,
                    followedByMe,
                    blockedByMe,
                    userBlockedMe
                }) {
        Object.defineProperty(this, '_data', {
            value: {
                userId,
                userName,
                fullName,
                profileImageUrl,
                followersCount,
                followingCount,
                postCount,
                description,
                followedByMe,
                blockedByMe,
                userBlockedMe,
            },
            writable: false,
            enumerable: false,
            configurable: false,
        });

        Object.defineProperties(this, {
            userId: {
                get() {
                    return this._data.userId;
                },
                set(value) {
                    if (typeof value !== "string") {
                        throw new TypeError("userId must be a string");
                    }
                    this._data.userId = value;
                },
                enumerable: true,
            },
            userName: {
                get() {
                    return this._data.userName;
                },
                set(value) {
                    if (typeof value !== "string") {
                        throw new TypeError("userName must be a string");
                    }
                    this._data.userName = value;
                },
                enumerable: true,
            },
            fullName: {
                get() {
                    return this._data.fullName;
                },
                set(value) {
                    if (typeof value !== "string") {
                        throw new TypeError("fullName must be a string");
                    }
                    this._data.fullName = value;
                },
                enumerable: true,
            },
            profileImageUrl: {
                get() {
                    return this._data.profileImageUrl;
                },
                set(value) {
                    if (typeof value !== "string") {
                        throw new TypeError("profileImageUrl must be a string");
                    }
                    this._data.profileImageUrl = value;
                },
                enumerable: true,
            },
            followersCount: {
                get() {
                    return this._data.followersCount;
                },
                set(value) {
                    if (typeof value !== "number") {
                        throw new TypeError("followersCount must be a number");
                    }
                    this._data.followersCount = value;
                },
                enumerable: true,
            },
            followingCount: {
                get() {
                    return this._data.followingCount;
                },
                set(value) {
                    if (typeof value !== "number") {
                        throw new TypeError("followingCount must be a number");
                    }
                    this._data.followingCount = value;
                },
                enumerable: true,
            },
            postCount: {
                get() {
                    return this._data.postCount;
                },
                set(value) {
                    if (typeof value !== "number") {
                        throw new TypeError("postCount must be a number");
                    }
                    this._data.postCount = value;
                },
                enumerable: true,
            },
            description: {
                get() {
                    return this._data.description;
                },
                set(value) {
                    if (typeof value !== "string") {
                        throw new TypeError("description must be a string");
                    }
                    this._data.description = value;
                },
                enumerable: true,
            },
            followedByMe: {
                get() {
                    return this._data.followedByMe;
                },
                set(value) {
                    if (typeof value === 'boolean') {
                        this._data.followedByMe = value;
                    } else {
                        throw new TypeError('followedByMe')
                    }
                },
                enumerable: true,
            },
            blockedByMe: {
                get() {
                    return this._data.blockedByMe;
                },
                set(value) {
                    if (typeof value === 'boolean') {
                        this._data.blockedByMe = value;
                    } else {
                        throw new TypeError('blockedByMe')
                    }
                },
                enumerable: true,
            },
            userBlockedMe: {
                get() {
                    return this._data.userBlockedMe;
                },
                set(value) {
                    if (typeof value === 'boolean') {
                        this._data.userBlockedMe = value;
                    } else {
                        throw new TypeError('userBlockedMe')
                    }
                },
                enumerable: true,
            },
        });
    }
}

export class Post {
    constructor({
                    id,
                    contentUrls,
                    type,
                    categoryId,
                    creatorId,
                    title,
                    description,
                    contentType,
                    likedByMe,
                    likeCount,
                    commentCount,
                    viewsCount,
                    shareCount,
                    commentIds,
                    addedCommentIds,
                    lastUpdated,
                }) {
        Object.defineProperty(this, '_data', {
            value: {
                id,
                contentUrls,
                type,
                categoryId,
                creatorId,
                title,
                description,
                contentType,
                likedByMe,
                likeCount,
                commentCount,
                viewsCount,
                shareCount,
                commentIds,
                addedCommentIds,
                lastUpdated,
            },
            writable: false,
            enumerable: false,
            configurable: false,
        });

        // Define setters and getters for each data property
        Object.defineProperties(this, {
            id: {
                get() {
                    return this._data.id;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.id = value;
                    } else {
                        throw new TypeError('id must be a string');
                    }
                },
                enumerable: true,
            },
            contentUrls: {
                get() {
                    return this._data.contentUrls;
                },
                set(value) {
                    if (Array.isArray(value)) {
                        this._data.contentUrls = value.map(content => ({
                            type: content.type,
                            urls: content.urls,
                            duration: content.duration,
                        }));
                    } else {
                        throw new TypeError('contentUrls must be an array');
                    }
                },
                enumerable: true,
            },
            categoryId: {
                get() {
                    return this._data.categoryId;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.categoryId = value;
                    } else {
                        throw new TypeError('categoryId must be a string');
                    }
                },
                enumerable: true,
            },
            creatorId: {
                get() {
                    return this._data.creatorId;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.creatorId = value;
                    } else {
                        throw new TypeError('creatorId must be a string');
                    }
                },
                enumerable: true,
            },
            title: {
                get() {
                    return this._data.title;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.title = value;
                    } else {
                        throw new TypeError('title must be a string');
                    }
                },
                enumerable: true,
            },
            description: {
                get() {
                    return this._data.description;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.description = value;
                    } else {
                        throw new TypeError('description must be a string');
                    }
                },
                enumerable: true,
            },
            contentType: {
                get() {
                    return this._data.contentType;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.contentType = value;
                    } else {
                        throw new TypeError('contentType must be a string');
                    }
                },
                enumerable: true,
            },
            likedByMe: {
                get() {
                    return this._data.likedByMe;
                },
                set(value) {
                    if (typeof value === 'boolean') {
                        this._data.likedByMe = value;
                    } else {
                        throw new TypeError('likedByMe')
                    }
                },
                enumerable: true,
            },
            likeCount: {
                get() {
                    return this._data.likeCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.likeCount = value;
                    } else {
                        throw new TypeError('likeCount must be a number');
                    }
                },
                enumerable: true,
            },
            commentCount: {
                get() {
                    return this._data.commentCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.commentCount = value;
                    } else {
                        throw new TypeError('commentCount must be a number');
                    }
                },
                enumerable: true,
            },
            viewsCount: {
                get() {
                    return this._data.viewsCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.viewsCount = value;
                    } else {
                        throw new TypeError('viewsCount must be a number');
                    }
                },
                enumerable: true,
            },
            shareCount: {
                get() {
                    return this._data.shareCount;
                },
                set(value) {
                    if (typeof value === 'number') {
                        this._data.shareCount = value;
                    } else {
                        throw new TypeError('shareCount must be a number');
                    }
                },
                enumerable: true,
            },
            commentIds: {
                get() {
                    return this._data.commentIds || [];
                },
                set(value) {
                    const isArray = Array.isArray(value);
                    const validValues = value.every(val => typeof val === 'string');
                    if (isArray && validValues) {
                        this._data.commentIds = value;
                    } else if (!validValues) {
                        throw new TypeError('commentIds every entry must be a string');
                    } else {
                        throw new TypeError('commentIds must be an array');
                    }
                },
                enumerable: true,
            },
            addedCommentIds: {
                get() {
                    return this._data.addedCommentIds || [];
                },
                set(value) {
                    if (Array.isArray(value)) {
                        if (!value.every(val => typeof val === 'string'))
                            throw new TypeError('array each value must be a string');
                        this._data.addedCommentIds = value;
                    } else {
                        throw new TypeError('addedCommentIds must be an array');
                    }
                },
                enumerable: true,
            },
            lastUpdated: {
                get() {
                    return this._data.lastUpdated;
                },
                set(value) {
                    if (typeof value === 'string') {
                        this._data.lastUpdated = value;
                    } else {
                        throw new TypeError('lastUpdated must be a string');
                    }
                },
                enumerable: true,
            },
        });
    }
}

export function mapToHashtag(rawHashtag) {
    return {
        id: rawHashtag.hashtagId,
        name: rawHashtag.title,
        description: rawHashtag.description,
        hashtagImageUrl: rawHashtag.image,
        postCount: rawHashtag.videoCount || 0,
    }
}

export function mapToComment(rawComment) {
    return new Comment({
        id: rawComment.commentRefId,
        description: rawComment.description,
        likeCount: rawComment.likeCount || 0,
        replyCount: rawComment.replyCount || 0,
        status: rawComment.status,
        creatorId: rawComment.userId,
        lastUpdated: SocialService._timeSince(rawComment.updatedAt),
    });
}

export function mapToUserProfile(rawUserProfile) {
    return new UserProfile({
        userId: rawUserProfile.userId,
        userName: rawUserProfile.fullName || rawUserProfile.userName,
        fullName: rawUserProfile.fullName,
        profileImageUrl: rawUserProfile.profileImageUrl,
        followersCount: rawUserProfile.followersCount || 0,
        followingCount: rawUserProfile.followingCount || 0,
        postCount: rawUserProfile.videoCount || 0,
        status: rawUserProfile.status || "ACTIVE",
        rewardPoints: rawUserProfile.rewardPoints || 0,
        description: rawUserProfile.description,
        followedByMe: false,
        blockedByMe: false,
        userBlockedMe: false,
    });
}

export function mapToPost(rawPost) {
    return new Post({
        id: rawPost._id || rawPost.postId,
        categoryId: rawPost.categoryId,
        contentUrls: rawPost.contentUrls,
        creatorId: rawPost.user.userId,
        title: rawPost.title,
        description: rawPost.description,
        contentType: rawPost.contentType || 'multi',
        likedByMe: rawPost.likedByMe ?? false,
        likeCount: rawPost.likeCount,
        commentCount: rawPost.commentCount,
        viewsCount: rawPost.viewsCount,
        shareCount: rawPost.shareCount,
        commentIds: rawPost?.commentIds || [],
        addedCommentIds: rawPost?.addedCommentIds || [],
        lastUpdated: SocialService._timeSince(rawPost.createdTimestamp)
    });
}

export const orderedMap = new OrderedMap();
export const orderedList = new OrderedList();

export class SocialService {
    constructor() {
        this.feedModule = new gluedin.GluedInFeedModule();
        this.activityTimelineModule = new gluedin.GluedInActivityTimeline();
        this.userModule = new gluedin.GluedInUserModule();
        this.authModule = new gluedin.GluedInAuthModule();
        this.hashtagModule = new gluedin.GluedInHashTag();
        this._pagination = new Map();
        this._meta = new Map();
        this._meta.set('commentsLimit', 5);
        this._meta.set('postsLimit', 6);
        this._meta.set('userPostsLimit', 6);
        this._meta.set('userFollowingList', 10);
        this._meta.set('userFollowerList', 10);
        this._meta.set('hashtagPostsLimit', 6);
        this._meta.set('postCategoriesLimit', 10);
        this._meta.set('topProfilesLimit', 10);
    }

    // ======================== POST START ============================ //
    async posts(count) {
        const type = 'posts';
        let limit = count ?? this._meta.get('postsLimit');
        let pagination = this.getPagination(type);
        if (pagination.completed) return {posts: [], users: []};

        // limit defaults to 6 entries.
        const responsePromise = SocialService._parseHTTP(this.feedModule.getFeedList({
            limit, offset: pagination.offset
        }));
        return this._parsePosts(responsePromise, type, limit);
    }

    async likePost(postId) {
        return SocialService._parseHTTP(this.activityTimelineModule.activityTimelineLike({assetId: postId}))
            .then(res => res.result);
    }

    async unlikePost(postId) {
        return SocialService._parseHTTP(this.activityTimelineModule.activityTimelineUnLike({assetId: postId}))
            .then(res => res.result);
    }

    async createMultiMediaPost(post) {
        let newErrors = SocialService._validateCreateMultiMediaPostPayload(post);
        let postData = {
            title: post?.title,
            description: post.description,
            type: "camera-upload",
            categoryId: post?.categoryId,
            categoryName: post?.categoryName,
            contentUrl: post.contentUrls,
            // just a placeholder
            s3Url: 'https://google.com'
        };

        if (Object.keys(newErrors).length === 0)
            return SocialService._parseHTTP(this.feedModule.uploadContent(postData))
                .then(res => res.result)
                .then(post => {
                    // we use postId instead of videoId attribute
                    post.postId = post.videoId;

                    let user = post.user;
                    let postIds = [post.videoId];
                    if (postIds && postIds.length > 0) {
                        return SocialService._parseHTTP(this.feedModule.Like(postIds))
                            .then(res => {
                                let likedPostIds = res.result ?? [];
                                let updatedPost = post;
                                if (likedPostIds.includes(post.postId))
                                    updatedPost.likedByMe = true;
                                return {
                                    post: mapToPost(updatedPost),
                                    user: mapToUserProfile(user),
                                };
                            });
                    } else {
                        return {
                            post: mapToPost(post),
                            user: mapToUserProfile(user),
                        };
                    }
                });
        else
            return Promise.reject(newErrors);
    }

    async createTextPost(post) {
        let postData = {
            contentType: 'text',
            title: post?.title,
            description: post.description,
            categoryId: post?.categoryId,
            categoryName: post?.categoryName,
            // just a placeholder.
            s3Url: 'https://google.com'
        };
        let newErrors = SocialService._validateCreateTextPostPayload(postData);

        if (Object.keys(newErrors).length === 0)
            return SocialService._parseHTTP(this.feedModule.uploadContent(postData))
                .then(res => res.result)
                .then(post => {
                    // we use postId and not videoId as attribute.
                    post.postId = post.videoId;

                    let user = post.user;
                    let postIds = [post.videoId];
                    if (postIds && postIds.length > 0) {
                        return SocialService._parseHTTP(this.feedModule.Like(postIds))
                            .then(res => {
                                let likedPostIds = res.result ?? [];
                                let updatedPost = post;
                                if (likedPostIds.includes(post.postId))
                                    updatedPost.likedByMe = true;
                                return {
                                    post: mapToPost(updatedPost),
                                    user: mapToUserProfile(user),
                                };
                            });
                    } else {
                        return {
                            post: mapToPost(post),
                            user: mapToUserProfile(user),
                        };
                    }
                });
        else
            return Promise.reject(newErrors);
    }

    async postCategories(count) {
        const type = 'postCategories';
        let limit = count ?? this._meta.get('postCategoriesLimit');
        let pagination = this.getPagination(type);
        if (pagination.completed) return [];

        return SocialService._parseHTTP(this.feedModule.getCategoryList({
            search: "", limit, offset: pagination.offset
        })).then(res => {
            const entriesCount = res.result.length;
            this.setPagination(type, entriesCount, limit);

            return res.result.map(category => ({
                categoryId: category.categoryId,
                categoryName: category.categoryName
            }));
        });
    }

    async reportCategories() {
        return SocialService._parseHTTP(this.feedModule.ReportReason())
            .then(res => {
                let {comment, user, video} = res.result;
                return {
                    commentReportCategories: comment,
                    userReportCategories: user,
                    postReportCategories: video,
                }
            });
    }

    async reportPost(reportingPostId, reason, reportingUserId, activeUserId) {
        return SocialService._parseHTTP(this.feedModule.Report({
            type: 'video',
            event: 'report',
            assetId: reportingPostId,
            userId: reportingUserId,
            actingUserId: activeUserId,
            reason,
        })).then(res => res.result);
    }

    // ======================== POST END ============================ //

    // ======================== COMMENT START ============================ //
    async comments(postId, count) {
        const type = postId;
        let limit = count ?? this._meta.get('commentsLimit');
        let pagination = this.getPagination(type);
        if (pagination.completed) return {comments: [], users: []};

        return SocialService._parseHTTP(this.activityTimelineModule.getActivityTimelineCommentList({
            limit, offset: pagination.offset, topicId: postId
        })).then(res => {
            const entriesCount = res.result.length;
            this.setPagination(type, entriesCount, limit);

            let comments = res.result.map(mapToComment);
            let users = res.result.map(mapToUserProfile);
            return {comments, users};
        });
    }

    async addComment(postId, message) {
        return SocialService._parseHTTP(this.activityTimelineModule.activityTimelineAddComment({
            assetId: postId, description: message
        })).then(res => {
            res.result.updatedAt = new Date().getTime();
            return mapToComment(res.result);
        });
    }

    async editComment(commentId, message, postId) {
        return SocialService._parseHTTP(this.feedModule.UpdateComment({
            assetId: postId, description: message, commentRefId: commentId
        })).then(res => {
            res.result.updatedAt = new Date().getTime();
            return mapToComment(res.result);
        });
    }

    async deleteComment(commentId, postId) {
        return SocialService._parseHTTP(this.activityTimelineModule.activityTimelineDeleteComment({
            assetId: postId, commentRefId: commentId
        })).then(res => res.result);
    }

    async reportComment(commentId, reason, postId, reportingUserId, activeUserId) {
        return SocialService._parseHTTP(this.feedModule.Report({
            type: 'comment',
            event: 'report',
            assetId: postId,
            postId: commentId,
            userId: reportingUserId,
            actingUserId: activeUserId,
            reason,
        })).then(res => res.result);
    }

    // ======================== COMMENT END ============================ //

    // ======================== USER START ============================ //
    async updateProfile(updatedProfile) {
        let { fullName, userName, description, profileImageUrl } = updatedProfile;
        return SocialService._parseHTTP(this.userModule.editUserProfile({
            fullName,
            userName,
            description,
            profileImageUrl
        })).then(res => mapToUserProfile(res.result));
    }

    async user(userId) {
        return SocialService._parseHTTP(this.userModule.getUserDetails(userId))
            .then(res => mapToUserProfile(res.result));
    }

    /*
    * { following: [], blockedByUser: [], blockedUser: [] }
    * */
    async userMeta() {
        return SocialService._parseHTTP(this.userModule.getUserMetaIds())
            .then(res => {
                // active user following these userIds.
                let following = res.result.following ?? [];
                // active user has been blocked by these userIds.
                let blockedByUser = res.result.blockedByUser ?? [];
                // active user has blocked these userIds.
                let blockedUser = res.result.blockedUser ?? [];
                return {following, blockedByUser, blockedUser};
            });
    }

    async topProfiles(count) {
        const type = 'topProfiles';
        let limit = count ?? this._meta.get('topProfilesLimit');
        let pagination = this.getPagination(type);
        if (pagination.completed) return [];

        return SocialService._parseHTTP(this.userModule.getTopProfiles({
            limit, offset: pagination.offset
        })).then(res => {
            const entriesCount = res.result.length;
            this.setPagination(type, entriesCount, limit);

            return res.result.map(mapToUserProfile);
        });
    }

    async blockUser(userId) {
        const formData = {
            blockUserId: userId,
            block: true
        };
        return SocialService._parseHTTP(this.userModule.blockUser(formData));
    }

    async unBlockUser(userId) {
        const formData = {
            blockUserId: userId,
            block: false
        };
        return SocialService._parseHTTP(this.userModule.blockUser(formData));
    }

    async reportUser(userId, reason, activeUserId) {
        return SocialService._parseHTTP(this.feedModule.Report({
            type: 'user',
            event: 'report',
            userId: userId,
            actingUserId: activeUserId,
            reason,
        }))
    }

    async followUser(userId) {
        const formData = {
            followingId: userId,
            isFollow: true,
        };
        return SocialService._parseHTTP(this.userModule.followUser(formData));
    }

    async unFollowUser(userId) {
        const formData = {
            followingId: userId,
            isFollow: false,
        };
        return SocialService._parseHTTP(this.userModule.followUser(formData));
    }

    async userFollowerList(userId) {
        const type = 'userFollowerList';
        let limit = count ?? this._meta.get('userFollowerListLimit');
        let pagination = this.getPagination(type);
        if (!pagination.id || pagination.id !== userId) {
            pagination = this.resetPagination(type, {id: userId});
        }
        if (pagination.completed) return {};

        return SocialService._parseHTTP(this.userModule.getFollowerList({
            userId: userId, limit, offset: pagination.offset
        })).then(res => res.result);
    }

    async userFollowingList(userId) {
        const type = 'userFollowingList';
        let limit = count ?? this._meta.get('userFollowingListLimit');
        let pagination = this.getPagination(type);
        if (!pagination.id || pagination.id !== userId) {
            pagination = this.resetPagination(type, {id: userId});
        }
        if (pagination.completed) return {};

        return SocialService._parseHTTP(this.userModule.getFollowingList({
            userId: userId, limit, offset: pagination.offset
        })).then(res => res.result);
    }

    async userPosts(userId, count) {
        const type = 'userPosts';
        let limit = count ?? this._meta.get('userPostsLimit');
        let pagination = this.getPagination(type);
        if (!pagination.id || pagination.id !== userId) {
            pagination = this.resetPagination(type, {id: userId});
        }
        if (pagination.completed) return {};

        const responsePromise = SocialService._parseHTTP(this.userModule.getUserVideoList({
            userId, limit, offset: pagination.offset
        }));
        return this._parsePosts(responsePromise, type, limit);
    }

    // ======================== USER END ============================ //

    // ======================== FILTER END ============================ //

    async hashtagInfo(hashtag) {
        return SocialService._parseHTTP(this.hashtagModule.getHashTagDetails({
            name: hashtag, offset: 1, limit: 6
        })).then(res => mapToHashtag(res.result.hashtag || {}));
    }

    async hashtagPosts(hashtag, count) {
        const type = 'hashtagPosts';
        let limit = count ?? this._meta.get('hashtagPostsLimit');
        let pagination = this.getPagination(type);
        if (!pagination.id || pagination.id !== hashtag) {
            pagination = this.resetPagination(type, {id: hashtag});
        }
        if (pagination.completed) return {posts: [], users: []};

        const responsePromise = SocialService._parseHTTP(this.hashtagModule.getHashTagVideoList({
            hashtagName: hashtag, limit, offset: pagination.offset
        }));
        return this._parsePosts(responsePromise, type, limit);
    }
    // ======================== FILTER END ============================ //

    static _retry(requestPromise) {
        try {
            return requestPromise;
        } catch {
            return requestPromise;
        }
    }

    static _parseHTTP(responsePromise) {
        function getMostSignificantDigit(number) {
            while (number >= 10) {
                number = Math.floor(number / 10);
            }
            return number;
        }

        return responsePromise.then(res => res.data)
            .then(data => {
                // specific check only for uploadContent gluedin api as it don't throw error on failure.
                if (data && 'statusCode' in data
                    && getMostSignificantDigit(data.statusCode) !== 2)
                    return Promise.reject({
                        message: data.message ?? data.statusMessage,
                        status: data.statusCode,
                    })
                return data;
            })
            .catch(response => {
                let statusCode = response.statusCode ?? response.status ?? 200;
                let error = {
                    message: response.message ?? response.statusMessage,
                    status: statusCode
                }
                return Promise.reject(error);
            });
    }

    static _parseFileType(type) {
        const imageRegex = /^image\//;
        const videoRegex = /^video\//;
        const audioRegex = /^audio\//;

        if (imageRegex.test(type)) {
            return 'image';
        } else if (videoRegex.test(type)) {
            return 'video';
        } else if (audioRegex.test(type)) {
            return 'audio';
        } else {
            return null; // Unknown file type
        }
    }

    static _validateCreateMultiMediaPostPayload(payload) {
        let newErrors = {};
        if (!payload.description)
            newErrors.description = "Please enter the description";
        else if (payload.description && payload.description.length > 500)
            newErrors.description = "Description should be less than 500 character long";

        if (!Array.isArray(payload.contentUrls)
            ||
            !payload.contentUrls.every(content => {
                return typeof content === 'object'
                    && typeof content.type === 'string'
                    && ['image', 'video'].includes(content.type)
                    && Array.isArray(content.urls)
                    && content.urls.every(url => typeof url === 'string')
            }) || payload.contentUrls.length > 15)
            newErrors.contentUrls = "Please check provided contentUrls";

        return newErrors;
    }

    static _validateCreateTextPostPayload(payload) {
        let newErrors = {};
        if (!payload.description)
            newErrors.description = "Please enter the description";
        else if (payload.description && payload.description.length > 500)
            newErrors.description = "Description should be less than 500 character long";

        return newErrors;
    }

    static _timeSince(timestamp, suffix = '', prefix = '') {
        const now = new Date();
        const diff = (now.getTime() - timestamp) / 1000; // convert to seconds

        if (diff < 60) {
            return prefix + Math.round(diff) + " sec" + suffix;
        } else if (diff < 60 * 60) {
            return prefix + Math.round(diff / 60) + " min" + suffix;
        } else if (diff < 60 * 60 * 24) {
            return prefix + Math.round(diff / (60 * 60)) + " h" + suffix;
        } else if (diff < 60 * 60 * 24 * 7) {
            return prefix + Math.round(diff / (60 * 60 * 24)) + " d" + suffix;
        } else if (diff < 60 * 60 * 24 * 30) {
            return prefix + Math.round(diff / (60 * 60 * 24 * 7)) + " w" + suffix;
        } else if (diff < 60 * 60 * 24 * 365) {
            return prefix + Math.round(diff / (60 * 60 * 24 * 30)) + " m" + suffix;
        } else {
            return prefix + Math.round(diff / (60 * 60 * 24 * 365)) + " y" + suffix;
        }
    }

    getPagination(type) {
        let pagination;
        if (this._pagination.has(type)) {
            pagination = this._pagination.get(type);
        } else {
            pagination = this.resetPagination(type);
        }
        return pagination;
    }

    setPagination(type, entriesCount, limit) {
        let pagination = this.getPagination(type);
        // Check if the number of entries in the response is less than the limit
        if (entriesCount < limit) {
            // All entries have been fetched,
            pagination.completed = true;
        } else {
            // increment by one only.
            pagination.offset += 1;
        }
        this._pagination.set(type, pagination);
        return pagination;
    }

    resetPagination(type, extra = {}) {
        let pagination = {offset: 1, completed: false, ...extra};
        this._pagination.set(type, pagination);
        return pagination;
    }

    _parsePosts(responsePromise, type, limit) {
        return responsePromise.then(res => {
            const entriesCount = res.result.length;
            this.setPagination(type, entriesCount, limit);

            // we use postId instead of videoId attribute
            let posts = res.result.map(post => ({
                ...post,
                postId: post.videoId,
            }));

            let users = posts.map(post => post.user);
            let postIds = posts.map(post => post.postId);

            if (postIds && postIds.length > 0) {
                return SocialService._parseHTTP(this.feedModule.Like(postIds))
                    .then(res => {
                        let likedPostIds = res.result ?? [];
                        let updatePosts = posts.map(post => {
                            if (likedPostIds.includes(post.postId))
                                post.likedByMe = true;
                            return post;
                        });
                        return {
                            posts: updatePosts.map(mapToPost),
                            users: users.map(mapToUserProfile),
                        };
                    });
            } else {
                return {
                    posts: posts.map(mapToPost),
                    users: users.map(mapToUserProfile),
                };
            }
        });
    }

    async uploadMedia(file) {
        return SocialService._parseHTTP(this.feedModule.getSignedUrl({
            type: SocialService._parseFileType(file.type)
        })).then(res => {
            let signedUrlResponse = res.result;
            let uploadUrl = signedUrlResponse.uploadUrl;
            let url = signedUrlResponse.url;
            let requestOptions = {
                method: "PUT",
                body: file,
                redirect: "follow",
                headers: {
                    'Content-Type': file.type,
                }
            };
            return fetch(uploadUrl, requestOptions)
                .then(response => url);
        });
    }

    async authenticate() {
        // back-side authentication
        const requestPromise = eliteService.socialAuthenticate()
            .then(authData => {
                this.authModule.setupSdk(authData);
                const {userinfo} = authData;
                const userInfo = mapToUserProfile(userinfo);
                return this.user(userInfo.userId);
            });
        return SocialService._retry(requestPromise)
            .catch(error => {
                redirect(error);
                notifyError(MESSAGES.SOCIAL_AUTH_FAILED);
                Promise.reject(error);
            });
    }
}